/**
 * FontAwesomeAdapter.js
 *
 * Font Awesome specific adapter implementation for TMS_IconFacade.
 * This file intentionally owns FA-specific parsing/mapping concerns.
 */
(function (global) {
	'use strict';

	var STYLE_ALIASES = {
		solid: 'solid',
		regular: 'regular',
		light: 'light',
		thin: 'thin',
		duotone: 'duotone',
		brands: 'brands',
		fas: 'solid',
		far: 'regular',
		fal: 'light',
		fat: 'thin',
		fad: 'duotone',
		fab: 'brands'
	};

	// Canonical token -> Font Awesome 5 icon suffix (kebab-case, without "fa-" prefix)
	var canonicalToFa5 = {
		check: 'check',
		close: 'times',
		save: 'save',
		edit: 'edit',
		trash: 'trash',
		add: 'plus-circle',
		info: 'info-circle',
		warning: 'exclamation-triangle',
		error: 'times-circle',
		success: 'check-circle',
		next: 'arrow-circle-right',
		prev: 'arrow-circle-left',
		refresh: 'sync',
		help: 'question-circle'
	};

	// Legacy FA4-ish token -> canonical token
	var legacyToCanonical = {
		times: 'close',
		'fa-times': 'close',
		'times-circle': 'error',
		'check-circle': 'success',
		'info-circle': 'info',
		'exclamation-triangle': 'warning',
		refresh: 'refresh',
		'arrow-circle-o-right': 'next',
		'arrow-circle-o-left': 'prev',
		save: 'save',
		pencil: 'edit',
		'question-circle': 'help',
		'circle-plus': 'add'
	};

	var fa4NameToFa5 = {
		cogs: 'cogs',
		'th-large': 'th-large',
		'clock-o': 'clock',
		hourglass: 'hourglass-half',
		arrows: 'arrows-alt',
		'exclamation-circle': 'exclamation-circle',
		'plus-circle': 'plus-circle',
		'minus-circle': 'minus-circle',
		'check-circle': 'check-circle',
		'star-o': 'star',
		'arrow-circle-o-right': 'arrow-circle-right'
	};

	// FA6+ name segments → FA5 Free (kebab-case without "fa-" prefix); applied before fa4NameToFa5.
	var modernToFa5 = {
		'arrows-rotate': 'sync',
		'arrow-rotate-left': 'undo',
		'arrow-rotate-right': 'redo',
		'rotate-right': 'redo',
		'rotate-left': 'undo',
		xmark: 'times',
		'circle-xmark': 'times-circle',
		'circle-check': 'check-circle',
		'circle-plus': 'plus-circle',
		'circle-minus': 'minus-circle',
		'circle-exclamation': 'exclamation-circle',
		'circle-question': 'question-circle',
		'circle-info': 'info-circle',
		'cloud-upload': 'cloud-upload-alt',
		'cloud-arrow-up': 'cloud-upload-alt',
		'clock-rotate-left': 'history',
		'clock-rotate-right': 'history',
		'trash-can': 'trash',
		'magnifying-glass': 'search',
		'magnifying-glass-plus': 'search-plus',
		'magnifying-glass-minus': 'search-minus',
		house: 'home',
		gear: 'cog',
		'pen-to-square': 'edit',
		'rectangle-list': 'list-alt',
		'mobile-screen-button': 'mobile-alt',
		'floppy-disk': 'save',
		'arrows-up-down-left-right': 'arrows-alt',
		'arrows-up-down': 'arrows-alt-v',
		'arrows-left-right': 'arrows-alt-h',
		maximize: 'expand-arrows-alt',
		'arrow-up-right-from-square': 'external-link-alt',
		'up-right-from-square': 'external-link-alt',
		'triangle-exclamation': 'exclamation-triangle',
		'file-lines': 'file-alt',
		'feather-pointed': 'feather-alt',
		sliders: 'sliders-h'
	};

	function mapFa5IconToken(token) {
		var mapped = modernToFa5[token] || token;
		return fa4NameToFa5[mapped] || mapped;
	}

	/**
	 * Normalizes style aliases to internal style keys (solid, regular, brands).
	 *
	 * When {@code fallback} is the empty string, unknown {@code value} returns {@code ''}
	 * so {@code parseInputIconClasses} does not treat bare canonical tokens (e.g. save, close)
	 * as style names. Matches PHP {@see FontAwesomeIconAdapter::normalizeStyle()}.
	 *
	 * @param {string} value style value, alias, or class fragment
	 * @param {string} [fallback] default style; use {@code ''} to mean "not a style" for unknown tokens
	 * @returns {string}
	 */
	function normalizeStyle(value, fallback) {
		var fb = fallback;
		if (fb === undefined || fb === null) {
			fb = 'solid';
		}
		var s = String(value || '').trim().toLowerCase();
		if (!s) {
			return fb !== '' ? fb : 'solid';
		}
		s = s.replace(/^fa-/, '');
		if (STYLE_ALIASES[s]) {
			return STYLE_ALIASES[s];
		}
		if (fb !== '') {
			return fb;
		}
		return '';
	}

	/**
	 * Identifies FA utility/modifier classes that should be preserved.
	 *
	 * @param {string} cls
	 * @returns {boolean}
	 */
	function isFaUtilityClass(cls) {
		return /^fa-(spin|pulse|beat|fade|beat-fade|bounce|shake|flip|flip-(horizontal|vertical|both)|rotate-\d+|fw|inverse|stack-\dx|xs|sm|lg|\dx|pull-(left|right)|border|li|ul|swap-opacity)$/.test(cls);
	}

	/**
	 * Lowercases existing fa-* utilities; maps bare modifier tokens (e.g. spin → fa-spin) when they are FA utilities.
	 * Other tokens pass through (e.g. Bootstrap text-*).
	 *
	 * @param {string} token
	 * @returns {string}
	 */
	function normalizeExtraClassToken(token) {
		var t = String(token || '').trim();
		if (!t) {
			return '';
		}
		var lower = t.toLowerCase();
		if (lower.indexOf('fa-') === 0) {
			return lower;
		}
		var candidate = 'fa-' + lower;
		if (isFaUtilityClass(candidate)) {
			return candidate;
		}
		return t;
	}

	/**
	 * Parses free-form icon input into style, icon candidate, and extra classes.
	 *
	 * Supports semantic tokens, legacy FA4 class strings, and FA5 / transitional class strings.
	 *
	 * @param {string} value
	 * @param {Object} [opt]
	 * @param {string} [opt.style] preferred style override
	 * @param {string} [opt.extraClasses] additional space-separated tokens (vendor-neutral modifiers like "spin", "fw"
	 *     or full FA utilities like "fa-spin"); bare modifiers map to fa-* in this adapter
	 * @returns {{style: string, iconCandidate: string, extras: string[]}}
	 */
	function parseInputIconClasses(value, opt) {
		var raw = String(value || '').trim();
		var parts = raw ? raw.split(/\s+/) : [];
		var style = normalizeStyle(opt && opt.style, 'solid');
		var iconCandidate = '';
		var extras = [];

		for (var i = 0; i < parts.length; i += 1) {
			var token = parts[i];
			var lower = token.toLowerCase();

			if (!lower || lower === 'fa' || lower === 'fa-classic') {
				continue;
			}

			var aliasStyle = normalizeStyle(lower, '');
			if (aliasStyle) {
				style = aliasStyle;
				continue;
			}

			if (lower.indexOf('fa-') === 0) {
				var stripped = lower.replace(/^fa-/, '');
				var prefixedStyle = normalizeStyle(stripped, '');
				if (prefixedStyle) {
					style = prefixedStyle;
					continue;
				}

				if (isFaUtilityClass(lower)) {
					extras.push(lower);
					continue;
				}

				if (!iconCandidate) {
					iconCandidate = stripped;
					continue;
				}
			}

			var trimmedTok = token.trim();
			var normalizedUtil = normalizeExtraClassToken(trimmedTok);
			if (normalizedUtil !== trimmedTok && isFaUtilityClass(normalizedUtil)) {
				extras.push(normalizedUtil);
				continue;
			}

			if (!iconCandidate) {
				iconCandidate = lower.replace(/^fa-/, '');
			} else {
				extras.push(token);
			}
		}

		if (opt && typeof opt.extraClasses === 'string' && opt.extraClasses.trim()) {
			var extraParts = opt.extraClasses.trim().split(/\s+/);
			for (var j = 0; j < extraParts.length; j += 1) {
				var n = normalizeExtraClassToken(extraParts[j]);
				if (n) {
					extras.push(n);
				}
			}
		}

		return {
			style: style,
			iconCandidate: iconCandidate,
			extras: extras
		};
	}

	/**
	 * Returns fallback canonical token from manifest, defaulting to "help".
	 *
	 * @param {Object} manifest
	 * @returns {string}
	 */
	function getFallbackCanonical(manifest) {
		return (manifest && manifest.fallback) || 'help';
	}

	/**
	 * Converts raw input into canonical token representation.
	 *
	 * @param {string} value
	 * @param {Object} [opt]
	 * @param {Object} manifest
	 * @returns {string}
	 */
	function toCanonicalToken(value, opt, manifest) {
		var parsed = parseInputIconClasses(value, opt);
		var cleaned = parsed.iconCandidate;
		if (!cleaned) {
			return getFallbackCanonical(manifest);
		}
		return legacyToCanonical[cleaned] || cleaned;
	}

	/**
	 * Converts canonical token to FA5 icon token.
	 *
	 * @param {string} canonical
	 * @param {Object} [opt]
	 * @param {Object} manifest
	 * @returns {string}
	 */
	function toToken(canonical, opt, manifest) {
		var c = canonical || getFallbackCanonical(manifest);
		var raw = canonicalToFa5[c] || c || canonicalToFa5[getFallbackCanonical(manifest)];
		return mapFa5IconToken(raw);
	}

	/**
	 * Builds full FA class string, preserving recognized modifier classes.
	 *
	 * @param {string} value
	 * @param {Object} [opt]
	 * @param {Object} manifest
	 * @returns {string}
	 */
	function styleKeyToFa5Prefix(style) {
		switch (style) {
			case 'regular':
				return 'far';
			case 'brands':
				return 'fab';
			default:
				return 'fas';
		}
	}

	function toClass(value, opt, manifest) {
		var parsed = parseInputIconClasses(value, opt);
		var canonical = parsed.iconCandidate
			? (legacyToCanonical[parsed.iconCandidate] || parsed.iconCandidate)
			: getFallbackCanonical(manifest);
		var faToken = mapFa5IconToken(
			canonicalToFa5[canonical] || canonical || canonicalToFa5[getFallbackCanonical(manifest)]
		);
		var classes = [styleKeyToFa5Prefix(parsed.style), 'fa-' + faToken];

		parsed.extras.forEach(function (cls) {
			if (cls && classes.indexOf(cls) === -1) {
				classes.push(cls);
			}
		});

		return classes.join(' ');
	}

	/**
	 * Returns FA token suitable for legacy renderer paths.
	 *
	 * @param {string} value
	 * @param {Object} [opt]
	 * @param {Object} manifest
	 * @returns {string}
	 */
	function toLegacyToken(value, opt, manifest) {
		return toToken(toCanonicalToken(value, opt, manifest), opt, manifest);
	}

	global.TMS_FontAwesomeAdapter = {
		toCanonicalToken: toCanonicalToken,
		toToken: toToken,
		toClass: toClass,
		toLegacyToken: toLegacyToken
	};
})(typeof window !== 'undefined' ? window : this);
